import {
  Avatar,
  Button,
  Card,
  Col,
  Flex,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Table,
  Tag,
  Typography,
} from "antd";
import React from "react";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useEffect } from "react";
import Spinner from "../../../../../common/spinner/Spinner";
import { appConstConfig } from "../../../../../../constants/config/appConstConfig";
import {
  getServiceProviderServicesAction,
  updateOrganizationServicesStatusAction,
} from "../../../../../../redux/services/service-providers/serviceProvidersServices";
import { colors } from "../../../../../../constants/colors";
import { dateObjToDispViewDate } from "../../../../../../utils/utils";
import { CustomTitle } from "../../../../../common/customTitle";
import Title from "antd/es/typography/Title";
import { getStaffAction } from "../../../../../../redux/services/staff/staffServices";
import { getUserProfileAction } from "../../../../../../redux/services/auth/authServices";
import useLocalStorage from "../../../../../../hooks/useLocalStorage";
import { appConfig } from "../../../../../../constants/config/appConfig";
import useDebounce from "../../../../../../hooks/useDebounce";
import { UserOutlined } from "@ant-design/icons";
import { userRoles } from "../../../../../../constants";
import Actions from "../../../../../../redux/actions";
import { baseRouteMaker, routes } from "../../../../../../constants/routeConsts";
import { toastService } from "../../../../../common/toast-messages/toast";

const DowngradeConfirmationDialog = ({ open, onClose }) => {
  /* i18n dependencies */
  const { t } = useTranslation();
  /* i18n dependencies */

  /* For navigating between pages */
  const navigate = useNavigate();
  /* For navigating between pages */

  /* form dependencies */
  const [form] = Form.useForm();
  /* form dependencies */

  /* dispatch an action */
  const dispatch = useDispatch();
  /* dispatch an action */

  /* loading dependencies */
  const [loading, setLoading] = useState(false);
  const [selectListLoader, setSelectListLoader] = useState(false);
  /* loading dependencies */

  const { setUserSession } = useLocalStorage(appConfig.SESSION_USER_INFO);

  /* id dependencies */
  const { staffs } = useSelector((state) => state.staff);
  /* id dependencies */

  /* table dependencies */
  const [columns, setColumns] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const { debouncedValue: debouncedSearchValue } = useDebounce(
    searchValue,
    500
  );
  const [pagination, setPagination] = useState({
    pageNo: appConstConfig.DEFAULT_TABLE_PAGE_NUMBER,
    pageSize: 50000,
  });
  const paginationObj = {
    total: staffs?.total,
    current: pagination?.pageNo,
    pageSize: pagination?.pageSize,
    onChange: (pageNo, pageSize) => setPagination({ pageNo, pageSize }),
    pageSizeOptions: appConstConfig.DEFAULT_PAGE_SIZE_OPTIONS_ARR,
    showSizeChanger: true,
  };
  const { selectedSubscriptionPlanDetails } = useSelector(
    (state) => state.serviceProviderSubscriptions
  );
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedRow, setSelectedRow] = useState([]);
  /* table dependencies */

  useEffect(() => {
    handleGetTableData();
  }, [open, pagination]);

  const handleGetTableData = () => {
    const dataToBeSent = {
      page: pagination?.pageNo,
      limit: pagination?.pageSize,
      search: debouncedSearchValue,
      status:"active"
    };
    dispatch(getStaffAction(dataToBeSent, setLoading));
    /* setting the user session based on roles */
    dispatch(getUserProfileAction(setUserSession));
  };

  /* dialog close dependencies */
  const handleDialogClose = () => {
    onClose();
    setSelectedRow([])
    setSelectedRowKeys([])
    form.resetFields();
  };
  /* dialog close dependencies */

  /* initial dependencies rendering */
  useEffect(() => {
    /* table column config */
    const tempTableColumns = [
      {
        title: t("staffList.name"),
        key: 1,
        render: (_, { profile_image }) => {
          return (
            <Flex gap="1rem" align="center">
              <Avatar
                src={profile_image}
                key={profile_image}
                icon={<UserOutlined />}
              />
              {`${_.first_name} ${_.last_name}`}
            </Flex>
          );
        },
      },
      {
        title: t("staffList.staff_type"),
        dataIndex: "role",
        key: 2,
        render: (_, { role }) => (
          <>
            <Tag
              bordered={false}
              style={{
                background:
                  role === userRoles.SERVICE_PROVIDER_ADMIN_ROLES.REGULAR_STAFF
                    ? colors.whiteSmoke
                    : role === "medical_staff"
                    ? colors.lightPastelBlue
                    : role === userRoles.SERVICE_PROVIDER_ADMIN_ROLES.DOCTOR
                    ? colors.darkSearGreen
                    : "",
                color:
                  role === userRoles.SERVICE_PROVIDER_ADMIN_ROLES.REGULAR_STAFF
                    ? colors.darkGrey
                    : role === "medical_staff"
                    ? colors.oceanBlue
                    : role === userRoles.SERVICE_PROVIDER_ADMIN_ROLES.DOCTOR
                    ? colors.limeGreen
                    : "",
              }}
            >
              {role === userRoles.SERVICE_PROVIDER_ADMIN_ROLES.REGULAR_STAFF
                ? "Regular"
                : role === "medical_staff"
                ? "Medical"
                : role === userRoles.SERVICE_PROVIDER_ADMIN_ROLES.DOCTOR
                ? "Doctor"
                : ""}
            </Tag>
          </>
        ),
      },
      {
        title: t("staffList.email"),
        dataIndex: "email",
        key: 3,
      },
      {
        title: t("staffList.phone"),
        dataIndex: "phone",
        key: 4,
        render: (phone) => `+${phone?.country_code}-${phone?.phone_number}`,
      },
      {
        title: t("staffList.availability"),
        key: 5,
        align: "center",
        render: (_, record) => {
          const getColorBasedOnStatus = (status) => {
            switch (status) {
              case "active":
                return { background: colors.lightTeal, color: colors.darkTeal };

              case "inactive":
                return {
                  background: colors.lightPinkishRed,
                  color: colors.darkMaroon,
                };

              case "suspended":
                return {
                  background: colors.lightPinkishRed,
                  color: colors.darkMaroon,
                };

              case "deleted":
                return {
                  background: colors.lightPinkishRed,
                  color: colors.darkMaroon,
                };

              default:
                break;
            }
          };
          return (
            <>
              <Tag
                bordered={false}
                style={{
                  ...getColorBasedOnStatus(record?.status),
                  textTransform: "capitalize",
                }}
              >
                {record?.status === "active"
                  ? t("CommonUtils.active")
                  : record?.status === "inactive"
                  ? t("CommonUtils.deleted")
                  : record?.status}
              </Tag>
            </>
          );
        },
      },
    ];
    /* table column config */
    setColumns(tempTableColumns);
  }, []);
  /* initial dependencies rendering */

  

  const onSelectChange = (changeableRowKeys, newSelectedRow) => {
    setSelectedRowKeys(changeableRowKeys);
    setSelectedRow(newSelectedRow);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange
  };


  /* on submit */
  const onSubmit = () => {
    const filteredStaffList = staffs?.data?.filter((element) => !selectedRow.some((elem) => elem._id === element._id)).filter((element) => element.status === "active");
    if(filteredStaffList?.length > selectedSubscriptionPlanDetails?.number_of_logins) {
        toastService.error(`You are only allowed to have ${selectedSubscriptionPlanDetails?.number_of_logins} staff members. Please adjust your selection.`)
        return;
    }
    dispatch(
        Actions.createAction(Actions.SELECTED_SUBSCRIPTION_PLAN_DETAILS, {
          selectedSubscriptionPlanDetails: {...selectedSubscriptionPlanDetails, user_ids_to_remove:selectedRow?.map((element) => element._id)}
        })
    );
    navigate(baseRouteMaker(routes.SUBSCRIPTION_PAYMENT));
  }

  return (
    <Modal
      open={open}
      onCancel={handleDialogClose}
      footer={false}
      confirmLoading={loading}
      style={{ top: 20 }}
      width={1000}
    >
      <Flex gap="1rem" vertical>
        <CustomTitle textDecoration="underline" justifyContent="center">
          Confirmation Dialog
        </CustomTitle>
        <Title level={2}>
        {`If you choose to downgrade your plan, note that the new plan accommodates fewer users. You will be allowed to keep ${selectedSubscriptionPlanDetails?.number_of_logins} staff members. Please select and retain your preferred staff members within this limit.`}
        </Title>

        {loading ? (
          <Spinner />
        ) : (
          <Table
            dataSource={staffs?.data?.filter((element) => element.status === "active")}
            columns={columns}
            // pagination={paginationObj}
            rowSelection={rowSelection}
          />
        )}
      </Flex>
      <Flex align="center" justify="center">
        <Button 
        type="primary"
            onClick={() => onSubmit()}
        >
            {t("CommonUtils.proceed")}
        </Button>
      </Flex>
    </Modal>
  );
};

export default DowngradeConfirmationDialog;
